import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// Types for employee profiles
export interface Employee {
  id: string;
  name: string;
  email: string;
  position: string;
  department: string;
  location: string;
  hireDate: string;
  manager: string;
  mentor?: string;
  profileImage?: string;
  status: "active" | "onLeave" | "terminated";
  onboardingProgress: number; // Percentage complete
  upcomingTouchpoints: {
    id: string;
    type: "pulseCheck" | "probationaryReview" | "preferredMethodChecklist";
    date: string;
    status: "pending" | "overdue";
  }[];
  // Additional employee details
  employeeDetails?: {
    phone?: string;
    address?: string;
    emergencyContact?: {
      name: string;
      relationship: string;
      phone: string;
    };
    documents?: {
      id: string;
      name: string;
      type: string;
      url: string;
      uploadDate: string;
    }[];
    notes?: string;
  };
}

// Filter options for the employee lists
interface FilterOptions {
  departments: string[];
  locations: string[];
  managers: string[];
  statuses: string[];
  dateRange: {
    startDate: string;
    endDate: string;
  };
}

// Main state interface
interface ManageEmployeesState {
  employees: Employee[];
  selectedEmployee: Employee | null;
  filterOptions: FilterOptions;
  selectedFilters: {
    department: string;
    location: string;
    manager: string;
    status: string;
    dateRange: {
      startDate: string;
      endDate: string;
    };
  };
  employeeMetrics: {
    totalEmployees: number;
    activeEmployees: number;
    onLeaveEmployees: number;
    terminatedEmployees: number;
    newHires30Days: number;
    newHires60Days: number;
    newHires90Days: number;
  };
  loading: boolean;
  error: string | null;
  successMessage: string | null;
  activeTab: string;
  profileView: "summary" | "details" | "documents" | "onboarding";
}

// Initial state
const initialState: ManageEmployeesState = {
  employees: [],
  selectedEmployee: null,
  filterOptions: {
    departments: [],
    locations: [],
    managers: [],
    statuses: ["active", "onLeave", "terminated"],
    dateRange: {
      startDate: "",
      endDate: "",
    },
  },
  selectedFilters: {
    department: "",
    location: "",
    manager: "",
    status: "",
    dateRange: {
      startDate: "",
      endDate: "",
    },
  },
  employeeMetrics: {
    totalEmployees: 0,
    activeEmployees: 0,
    onLeaveEmployees: 0,
    terminatedEmployees: 0,
    newHires30Days: 0,
    newHires60Days: 0,
    newHires90Days: 0,
  },
  loading: false,
  error: null,
  successMessage: null,
  activeTab: "all-employees",
  profileView: "summary",
};

// Create the slice
const manageEmployeesSlice = createSlice({
  name: "manageEmployees",
  initialState,
  reducers: {
    // Employee-related actions
    setEmployees: (state, action: PayloadAction<Employee[]>) => {
      state.employees = action.payload;
    },
    addEmployee: (state, action: PayloadAction<Employee>) => {
      state.employees.push(action.payload);
    },
    updateEmployee: (state, action: PayloadAction<Employee>) => {
      const index = state.employees.findIndex(
        (emp) => emp.id === action.payload.id
      );
      if (index >= 0) {
        state.employees[index] = action.payload;
      }
    },
    setSelectedEmployee: (state, action: PayloadAction<Employee | null>) => {
      state.selectedEmployee = action.payload;
    },

    // Document management
    addEmployeeDocument: (
      state,
      action: PayloadAction<{
        employeeId: string;
        document: {
          id: string;
          name: string;
          type: string;
          url: string;
          uploadDate: string;
        };
      }>
    ) => {
      const { employeeId, document } = action.payload;
      const employeeIndex = state.employees.findIndex(
        (emp) => emp.id === employeeId
      );

      if (employeeIndex >= 0) {
        if (!state.employees[employeeIndex].employeeDetails) {
          state.employees[employeeIndex].employeeDetails = {};
        }
        if (!state.employees[employeeIndex].employeeDetails!.documents) {
          state.employees[employeeIndex].employeeDetails!.documents = [];
        }
        state.employees[employeeIndex].employeeDetails!.documents!.push(
          document
        );

        // Also update selected employee if it's the same one
        if (state.selectedEmployee?.id === employeeId) {
          if (!state.selectedEmployee.employeeDetails) {
            state.selectedEmployee.employeeDetails = {};
          }
          if (!state.selectedEmployee.employeeDetails.documents) {
            state.selectedEmployee.employeeDetails.documents = [];
          }
          state.selectedEmployee.employeeDetails.documents.push(document);
        }
      }
    },

    removeEmployeeDocument: (
      state,
      action: PayloadAction<{ employeeId: string; documentId: string }>
    ) => {
      const { employeeId, documentId } = action.payload;
      const employeeIndex = state.employees.findIndex(
        (emp) => emp.id === employeeId
      );

      if (
        employeeIndex >= 0 &&
        state.employees[employeeIndex].employeeDetails?.documents
      ) {
        state.employees[employeeIndex].employeeDetails!.documents =
          state.employees[employeeIndex].employeeDetails!.documents!.filter(
            (doc) => doc.id !== documentId
          );

        // Also update selected employee if it's the same one
        if (
          state.selectedEmployee?.id === employeeId &&
          state.selectedEmployee.employeeDetails?.documents
        ) {
          state.selectedEmployee.employeeDetails.documents =
            state.selectedEmployee.employeeDetails.documents.filter(
              (doc) => doc.id !== documentId
            );
        }
      }
    },

    // Filter-related actions
    setFilterOptions: (state, action: PayloadAction<FilterOptions>) => {
      state.filterOptions = action.payload;
    },
    setSelectedFilters: (
      state,
      action: PayloadAction<
        Partial<{
          department: string;
          location: string;
          manager: string;
          status: string;
          dateRange: {
            startDate: string;
            endDate: string;
          };
        }>
      >
    ) => {
      state.selectedFilters = { ...state.selectedFilters, ...action.payload };
    },
    resetFilters: (state) => {
      state.selectedFilters = initialState.selectedFilters;
    },

    // Metrics actions
    setEmployeeMetrics: (
      state,
      action: PayloadAction<
        Partial<{
          totalEmployees: number;
          activeEmployees: number;
          onLeaveEmployees: number;
          terminatedEmployees: number;
          newHires30Days: number;
          newHires60Days: number;
          newHires90Days: number;
        }>
      >
    ) => {
      state.employeeMetrics = { ...state.employeeMetrics, ...action.payload };
    },

    // UI state actions
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setSuccessMessage: (state, action: PayloadAction<string | null>) => {
      state.successMessage = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    setActiveTab: (state, action: PayloadAction<string>) => {
      state.activeTab = action.payload;
    },
    setProfileView: (
      state,
      action: PayloadAction<"summary" | "details" | "documents" | "onboarding">
    ) => {
      state.profileView = action.payload;
    },

    // Reset state actions
    resetManageEmployeesState: (state) => {
      // Reset all state to initial values
      Object.assign(state, initialState);
    },

    // Memory cleanup actions
    limitEmployees: (state, action: PayloadAction<number>) => {
      const maxEmployees = action.payload || 1000;
      if (state.employees.length > maxEmployees) {
        // Keep the most recently hired employees
        state.employees = state.employees
          .sort((a, b) => new Date(b.hireDate).getTime() - new Date(a.hireDate).getTime())
          .slice(0, maxEmployees);
      }
    },
    clearEmployeeDocuments: (state) => {
      // Clear document data while preserving employee records
      state.employees = state.employees.map(emp => ({
        ...emp,
        employeeDetails: emp.employeeDetails ? {
          ...emp.employeeDetails,
          documents: []
        } : undefined
      }));
    },
    clearEmployeeImages: (state) => {
      // Clear profile images to save memory
      state.employees = state.employees.map(emp => ({
        ...emp,
        profileImage: undefined
      }));
    },
    clearSelectedEmployeeData: (state) => {
      state.selectedEmployee = null;
    },
    resetEmployeeFilters: (state) => {
      state.selectedFilters = {
        department: "",
        location: "",
        manager: "",
        status: "",
        dateRange: {
          startDate: "",
          endDate: "",
        },
      };
    },
  },
});

// Export actions
export const {
  // Employee actions
  setEmployees,
  addEmployee,
  updateEmployee,
  setSelectedEmployee,

  // Document management
  addEmployeeDocument,
  removeEmployeeDocument,

  // Filter actions
  setFilterOptions,
  setSelectedFilters,
  resetFilters,

  // Metrics actions
  setEmployeeMetrics,

  // UI state actions
  setLoading,
  setError,
  setSuccessMessage,
  clearError,
  clearSuccessMessage,
  setActiveTab,
  setProfileView,

  // Reset actions
  resetManageEmployeesState,

  // Memory cleanup actions
  limitEmployees,
  clearEmployeeDocuments,
  clearEmployeeImages,
  clearSelectedEmployeeData,
  resetEmployeeFilters,
} = manageEmployeesSlice.actions;

export default manageEmployeesSlice.reducer;
